import { useEffect, useState } from "react";
import {
  Navigate,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import "./OrganizationSubscriptionPage.css";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { useAuth } from "../../../hooks/useAuth";
import { usePlans } from "../../../hooks/usePlans";
import { useOrgEntitlements } from "../../../hooks/useOrgEntitlements";
import { getOrganizationByIdAsync } from "../../../api/OrganizationApi";
import {
  activateSubscriptionAsync,
  cancelSubscriptionAsync,
  extendSubscriptionAsync,
} from "../../../api/SubscriptionApi";
import { Constants } from "../../../constants/constants";
import { OrganizationResponse } from "../../../models/OrganizationModels";
import {
  ActivateSubscriptionRequest,
  CancelSubscriptionRequest,
  ExtendSubscriptionRequest,
  Plan,
} from "../../../models/SubscriptionModels";
import SubscriptionInfoCard from "../../../components/SubscriptionInfoCard";
import ActivateSubscriptionForm from "../../../components/ActivateSubscriptionForm";
import ExtendSubscriptionForm from "../../../components/ExtendSubscriptionForm";
import ConfirmDialog from "../../../components/ConfirmDialog";
import Toast from "../../../components/Toast";

const PLATFORM_ONLY_MESSAGE =
  "Only Platform Owner can manage subscriptions.";

const NO_ACTIVE_MESSAGE = "No active subscription found for this organization";

const OrganizationSubscriptionPage = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialAction = searchParams.get("action");

  const [organization, setOrganization] = useState<OrganizationResponse | null>(
    null,
  );
  const [orgLoading, setOrgLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [cancelNotes, setCancelNotes] = useState("");
  const [cancelling, setCancelling] = useState(false);
  const [cancelUnavailable, setCancelUnavailable] = useState(false);
  const [mode, setMode] = useState<"extend" | "change">(
    initialAction === "change" ? "change" : "extend",
  );
  const [toast, setToast] = useState<{
    message: string;
    type: "success" | "error" | "info";
  } | null>(null);

  const {
    plans,
    loading: plansLoading,
    reload: reloadPlans,
  } = usePlans(true);
  const {
    entitlements,
    loading: entitlementsLoading,
    reload: reloadEntitlements,
  } = useOrgEntitlements(organizationId);

  const token = localStorage.getItem(Constants.ACCESS_TOKEN) || "";

  useEffect(() => {
    if (!organizationId) return;
    let cancelled = false;
    setOrgLoading(true);
    getOrganizationByIdAsync(token, organizationId)
      .then((res) => {
        if (!cancelled && res.success && res.result) {
          setOrganization(res.result);
        }
      })
      .catch((err) => console.error("Failed to load organization:", err))
      .finally(() => {
        if (!cancelled) setOrgLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  useEffect(() => {
    if (user?.isPlatformOwner) {
      reloadPlans();
    }
  }, [user?.isPlatformOwner, reloadPlans]);

  if (!user?.isPlatformOwner) {
    return <Navigate to="/dashboard" replace />;
  }

  const activePlan: Plan | null =
    plans.find((plan) => plan.id === entitlements?.planId) ?? null;

  const handleSectionChange = (key: string) => {
    navigate(`/dashboard?section=${encodeURIComponent(key)}`);
  };

  const refreshAll = async () => {
    await Promise.all([reloadEntitlements(), reloadPlans()]);
  };

  const handleActivate = async (request: ActivateSubscriptionRequest) => {
    setSubmitting(true);
    try {
      const res = await activateSubscriptionAsync(token, request);
      if (res.success && res.result) {
        setToast({
          message:
            mode === "change"
              ? "Plan changed successfully!"
              : "Subscription activated successfully!",
          type: "success",
        });
        await refreshAll();
      } else if (res.responseCode === 403) {
        setToast({ message: PLATFORM_ONLY_MESSAGE, type: "error" });
      } else {
        setToast({
          message: res.message || "Failed to activate subscription",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to activate subscription. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleExtend = async (request: ExtendSubscriptionRequest) => {
    setSubmitting(true);
    try {
      const res = await extendSubscriptionAsync(token, request);
      if (res.success && res.result) {
        setToast({
          message: "Subscription extended successfully!",
          type: "success",
        });
        await refreshAll();
      } else if (res.responseCode === 403) {
        setToast({ message: PLATFORM_ONLY_MESSAGE, type: "error" });
      } else {
        setToast({
          message: res.message || "Failed to extend subscription",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to extend subscription. Please try again.",
        type: "error",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = async () => {
    if (!organizationId) return;
    const request: CancelSubscriptionRequest = {
      organizationId,
      notes: cancelNotes.trim() || null,
    };
    setCancelling(true);
    try {
      const res = await cancelSubscriptionAsync(token, request);
      if (res.success) {
        setCancelOpen(false);
        setCancelNotes("");
        setCancelUnavailable(false);
        setToast({
          message: res.message || "Subscription cancelled successfully!",
          type: "success",
        });
        await refreshAll();
      } else if (res.responseCode === 403) {
        setToast({ message: PLATFORM_ONLY_MESSAGE, type: "error" });
      } else if (
        res.message &&
        res.message.toLowerCase().includes("no active subscription")
      ) {
        setCancelOpen(false);
        setCancelUnavailable(true);
        setToast({
          message: res.message || NO_ACTIVE_MESSAGE,
          type: "error",
        });
      } else {
        setToast({
          message: res.message || "Failed to cancel subscription",
          type: "error",
        });
      }
    } catch (err) {
      console.error(err);
      setToast({
        message: "Failed to cancel subscription. Please try again.",
        type: "error",
      });
    } finally {
      setCancelling(false);
    }
  };

  const hasActive = entitlements?.hasActiveSubscription === true;

  return (
    <DashboardLayout
      activeSection="subscriptions"
      onSectionChange={handleSectionChange}
    >
      <div className="org-sub-page">
        <div className="org-sub-header">
          <div>
            <h2>Subscription — {organization?.name ?? "Organization"}</h2>
            <p>Manage the paid plan for this organization.</p>
          </div>
          <button
            className="org-sub-btn org-sub-btn--secondary"
            onClick={() => navigate("/dashboard?section=organizations")}
          >
            ← Back to organizations
          </button>
        </div>

        {orgLoading ? (
          <div className="org-sub-empty">Loading organization…</div>
        ) : (
          <div className="org-sub-grid">
            <section className="org-sub-card">
              <SubscriptionInfoCard
                entitlements={entitlements}
                loading={entitlementsLoading}
                onRefresh={reloadEntitlements}
              />
            </section>

            <section className="org-sub-card">
              <div className="org-sub-card__head">
                <h3 className="org-sub-card__title">
                  {hasActive ? "Manage subscription" : "Activate subscription"}
                </h3>
                {hasActive && !entitlementsLoading && (
                  <button
                    className="org-sub-btn org-sub-btn--danger"
                    disabled={cancelling || cancelUnavailable}
                    title={
                      cancelUnavailable
                        ? NO_ACTIVE_MESSAGE
                        : "Cancel this organization's subscription"
                    }
                    onClick={() => setCancelOpen(true)}
                  >
                    {cancelling ? (
                      <>
                        <span className="org-sub-spinner" aria-hidden="true" />
                        Cancelling…
                      </>
                    ) : (
                      "Cancel subscription"
                    )}
                  </button>
                )}
              </div>
              {cancelUnavailable && hasActive && (
                <p className="org-sub-hint">
                  {NO_ACTIVE_MESSAGE}. Refresh the page after activating a new
                  subscription.
                </p>
              )}
              {entitlementsLoading ? (
                <div className="org-sub-empty">Loading subscription…</div>
              ) : hasActive ? (
                <>
                  <div className="sub-mode-tabs" role="tablist">
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === "extend"}
                      className={
                        mode === "extend"
                          ? "sub-mode-tab sub-mode-tab--active"
                          : "sub-mode-tab"
                      }
                      onClick={() => setMode("extend")}
                    >
                      Extend / renew
                    </button>
                    <button
                      type="button"
                      role="tab"
                      aria-selected={mode === "change"}
                      className={
                        mode === "change"
                          ? "sub-mode-tab sub-mode-tab--active"
                          : "sub-mode-tab"
                      }
                      onClick={() => setMode("change")}
                    >
                      Change plan
                    </button>
                  </div>

                  {mode === "extend" ? (
                    <ExtendSubscriptionForm
                      organizationId={organizationId as string}
                      entitlements={entitlements}
                      plan={activePlan}
                      submitting={submitting}
                      onSubmit={handleExtend}
                    />
                  ) : (
                    <>
                      <p className="org-sub-hint">
                        Changing the plan replaces the current subscription and
                        takes effect immediately. Any unused days of the old
                        plan are not refunded.
                      </p>
                      <ActivateSubscriptionForm
                        organization={organization}
                        plans={plans}
                        plansLoading={plansLoading}
                        submitting={submitting}
                        submitLabel="Change plan"
                        submittingLabel="Saving…"
                        onSubmit={handleActivate}
                      />
                    </>
                  )}
                </>
              ) : (
                <ActivateSubscriptionForm
                  organization={organization}
                  plans={plans}
                  plansLoading={plansLoading}
                  submitting={submitting}
                  submitLabel="Activate subscription"
                  submittingLabel="Activating…"
                  onSubmit={handleActivate}
                />
              )}
            </section>
          </div>
        )}
      </div>

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {cancelOpen && (
        <ConfirmDialog
          title="Cancel Subscription"
          message="Are you sure you want to cancel this organization's subscription? This action cannot be undone."
          confirmLabel="Yes, Cancel"
          cancelLabel="Keep subscription"
          busyLabel="Cancelling…"
          busy={cancelling}
          onCancel={() => setCancelOpen(false)}
          onConfirm={handleCancel}
        >
          <div className="cancel-note">
            <label htmlFor="cancel-notes">Reason (optional)</label>
            <textarea
              id="cancel-notes"
              rows={2}
              value={cancelNotes}
              disabled={cancelling}
              onChange={(e) => setCancelNotes(e.target.value)}
              placeholder="Why is this subscription being cancelled?"
            />
          </div>
        </ConfirmDialog>
      )}
    </DashboardLayout>
  );
};

export default OrganizationSubscriptionPage;